<script setup lang="ts">
import {
  ClockIcon,
  MapIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import type { Tour } from "../types/general"; // Adjust the path as necessary
import BookingOptionsParticipants from "./BookingOptionsParticipants.vue";
import IconMountain from "~/assets/icons/iconMountain.vue";
import IconTriangle from "~/assets/icons/iconTriangle.vue";

const props = defineProps({
  open: { type: Boolean },
  tour: {
    type: Object as () => Tour,
    default: () => ({ itinerary: [{ time: "" }] }),
  },
});

const product_id = props.tour?.id;
// const optionsPrice = props.tour?.prices;

const optionsPrices = props.tour?.prices ?? [];
const cartStore = useCartStore();
const itinerary = ref(cartStore.getItinerary(product_id));
// itimerary start from 1

// Define the custom event 'close'
const emit = defineEmits(["close"]);

// date picker
// use String as model
const selected_date = ref("");
const formatter = ref({
  date: "DD MMM YYYY",
  month: "MMM",
});

const startFrom = ref(new Date());
const dDate = (date: Date) => {
  return date < new Date();
};

// Select Options
const add_ons = ref();
const pickup_type = ref();
const guide_language = ref();
const additional_tour = ref();

// Function to set up watchers
const setupWatcher = (property: Ref<any>, keyName: any) => {
  watch(property, (newValue: any) => {
    cartStore.addOptions(props.tour.id, keyName, newValue);
    // check if the property is add_ons and additional_tour
    if (keyName === "add_ons" || keyName === "additional_tour") {
      cartStore.insertItinerary(props.tour.id, newValue);
      // recall itinerary
      itinerary.value = cartStore.getItinerary(product_id);
    }
  });
};

// Set up watchers for each property
setupWatcher(selected_date, "selected_date");
setupWatcher(add_ons, "add_ons");
setupWatcher(pickup_type, "pickup_type");
setupWatcher(guide_language, "guide_language");
setupWatcher(additional_tour, "additional_tour");

// handle Verification All field must be filled before add to cart
const isValid = ref(true);
const checkAllFields = ref(false);
const participants = ref<number | null>(null);
const isAnyParticipantSelected = computed(() => {
  return participants.value && participants.value > 0;
});
const handleVerification = () => {
  const validAddOns = !props.tour?.addons?.length || !!add_ons.value;
  const validPickup = !props.tour?.addpickups?.length || !!pickup_type.value;
  const validGuide = !props.tour?.addguides?.length || !!guide_language.value;
  const validTour = !props.tour?.addtours?.length || !!additional_tour.value;

  return (
    !!selected_date.value &&
    validAddOns &&
    validPickup &&
    validGuide &&
    validTour &&
    isAnyParticipantSelected.value
  );
};

// handle cart
const router = useRouter();

const handleAddToCart = () => {
  const valid = handleVerification();

  if (!valid) {
    isValid.value = false;
    checkAllFields.value = true;
    setTimeout(() => {
      isValid.value = true;
    }, 3000);
    return;
  }

  router.push("/cart");
};

watch(
  [selected_date, add_ons, pickup_type, guide_language, additional_tour, participants],
  ([date, addons, pickup, guide, tour, participantsCount]: any) => {
    if (handleVerification()) {
      checkAllFields.value = false;
    }
  }
);

// dialog input form
const isItineraryDetailOpen = ref(false);

const lastTime = computed(() => {
  // Count total duration
  const totalDuration = itinerary.value.reduce((acc: any, item: any) => {
    return acc + (Number(item.duration) || 0);
  }, 0);

  // Convert total duration to hours and minutes
  const hours = Math.floor(totalDuration / 60);
  const minutes = totalDuration % 60;

  // Get start time from props
  const startTime = (props.tour.itinerary[0] as any).time;
  const startTimeArray = startTime.split(":");
  const startHour = Number(startTimeArray[0]);
  const startMinute = Number(startTimeArray[1]);

  // Calculate end time
  let endHour = startHour + hours;
  let endMinute = startMinute + minutes;

  // Handle minute overflow
  if (endMinute >= 60) {
    endHour += Math.floor(endMinute / 60);
    endMinute = endMinute % 60;
  }
  // Format the result as "H:MM"
  return `${endHour}:${endMinute.toString().padStart(2, "0")}`;
});
</script>

<template>
  <!-- Verification All field must be filled before add to cart -->
  <HeadlessTransitionRoot as="template" :show="open">
    <HeadlessDialog class="relative z-30">
      <div class="fixed inset-0" />

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="fixed inset-y-0 right-0 flex max-w-full pointer-events-none">
            <HeadlessTransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full" enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0"
              leave-to="translate-x-full">
              <HeadlessDialogPanel class="w-screen max-w-md pointer-events-auto">
                <div class="flex flex-col h-full bg-white divide-y divide-gray-200 shadow-xl">
                  <div class="relative flex flex-col flex-1 min-h-0 pt-6 overflow-y-scroll">
                    <div v-if="!isValid"
                      class="sticky z-50 flex items-center gap-3 px-6 py-4 mx-2 text-red-800 bg-orange-100 border border-orange-300 shadow-lg animate-fade-in top-4 rounded-xl md:mx-4">
                      <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728"></path>
                      </svg>
                      <span class="font-medium">Please fill all fields before adding to cart</span>
                    </div>
                    <!-- End Verification All field must be filled before add to cart -->
                    <div class="px-4 sm:px-6">
                      <div class="flex items-start justify-between">
                        <HeadlessDialogTitle class="text-base font-semibold leading-6 text-gray-900">Booking Options
                        </HeadlessDialogTitle>
                        <div class="flex items-center ml-3 h-7">
                          <button type="button" class="relative text-gray-400 bg-white rounded-md hover:text-gray-500"
                            @click="emit('close')">
                            <span class="absolute -inset-2.5" />
                            <span class="sr-only">Close panel</span>
                            <XMarkIcon class="w-6 h-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="relative flex-col flex-1 px-4 mt-6 space-y-4 sm:px-6">
                      <h1 class="text-sm font-medium leading-6 text-gray-600">
                        {{ tour?.title }}
                      </h1>
                      <!-- handle Verification All field must be filled before add to cart select date -->
                      <div>
                        <span class="text-red-800" v-if="checkAllFields && !selected_date">Date cannot be empty!</span>
                      </div>
                      <div class="justified-between flex items-center rounded-md border-[0.5px] border-[#DDD] p-4">
                        <h2 class="w-full text-base font-semibold text-gray-800">
                          Select Date
                        </h2>
                        <div class="w-full">
                          <client-only>
                            <VueTailwindDatepicker :formatter="formatter" v-model="selected_date" :disable-date="dDate"
                              as-single placeholder="Select date" :start-from="startFrom" />
                          </client-only>
                        </div>
                      </div>
                      <!-- Price -->
                      <div>
                        <span v-if="
                          checkAllFields &&
                          (!participants || participants === 0)
                        " class="text-sm text-red-800">
                          Participants cannot be empty!
                        </span>
                      </div>
                      <div class="rounded-md border-[0.5px] border-[#DDD] p-4">
                        <h2 class="w-full mb-4 text-base font-semibold text-gray-800">
                          Participants
                        </h2>
                        <BookingOptionsParticipants :product_id="props.tour?.id" :optionsPrices="optionsPrices"
                          v-model="participants" />
                      </div>
                      <!-- End Price -->
                      <!-- Options -->
                      <div class="flex flex-col rounded-md border-[0.5px] border-[#DDD] p-4">
                        <h2 class="w-full mb-4 text-base font-semibold text-gray-800">
                          Select Options
                        </h2>
                        <span class="text-red-800" v-if="checkAllFields && !additional_tour && tour?.addtours?.length > 0">Tour Options cannot be
                          empty!</span>
                        <SelectOptions title="Tours Options" v-model="additional_tour"
                          :availableOptions="tour?.addtours" />
                        <span class="text-red-800" v-if="checkAllFields && !guide_language && tour?.addguides?.length > 0">Driver/guide language cannot
                          be empty!</span>
                        <SelectOptions title="Driver/guide language" v-model="guide_language"
                          :availableOptions="tour.addguides" />
                        <span class="text-red-800" v-if="checkAllFields && !pickup_type && tour?.addpickups?.length > 0">Pickup type cannot be
                          empty!</span>
                        <SelectOptions title="Pickup type" v-model="pickup_type" :availableOptions="tour?.addpickups" />

                        <!-- Tampilkan lokasi hanya jika pickup_type bukan 'No Pickup' -->
                        <Transition name="fade-slide" appear>
                          <div v-if="pickup_type && pickup_type.name === 'No Pickup'"
                            class="flex flex-col items-start justify-between gap-2 p-4 mb-3 border rounded-lg bg-gray-50 sm:flex-row sm:items-center">
                            <div class="text-sm text-gray-700">
                              <span class="font-medium">Location:</span> {{ tour?.location_name ? tour?.location_name :
                              "-" }}
                            </div>
                            <div>
                              <a :href="tour?.google_map_url" target="_blank" rel="noopener noreferrer"
                                class="text-sm text-blue-600 hover:underline">
                                View on google maps
                              </a>
                            </div>
                          </div>
                        </Transition>
                        <span class="text-red-800" v-if="checkAllFields && !add_ons && tour?.addons?.length > 0">Add-ons cannot be empty!</span>
                        <SelectOptions title="Add-ons" v-model="add_ons" :availableOptions="tour.addons" />
                      </div>
                    </div>
                    <div class="flex items-center justify-center mt-6">
                      <component :is="IconTriangle" />
                    </div>
                    <div class="bg-[#F3F9FF] px-4 py-4">
                      <h2 class="w-full mb-4 text-base font-semibold text-gray-800">
                        Itinerary
                      </h2>
                      <div v-if="itinerary.length !== 0">
                        <div class="flex mb-4 space-x-2 text-sm">
                          <div>
                            <component :is="IconMountain" class="w-5 h-5 top-1 text-primary" aria-hidden="true" />
                          </div>
                          <div>
                            <ul class="space-y-2">
                              <li v-for="item in itinerary.slice(1)">
                                {{ item.name }}
                              </li>
                            </ul>
                          </div>
                        </div>
                        <button @click="
                          isItineraryDetailOpen = !isItineraryDetailOpen
                          " class="flex justify-center w-full py-2 mb-4 text-sm text-center border rounded-md">
                          <MapIcon class="w-5 h-5 mr-2" /> See itinerary details
                        </button>
                        <div v-if="isItineraryDetailOpen" class="px-8 mb-4">
                          <ul class="space-y-2">
                            <li v-for="item in itinerary" :key="item.name">
                              <span class="inline-block w-10">{{ item.time }}
                              </span>
                              {{ item.name }}
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div class="flex mb-4 space-x-2 text-sm">
                        <div>
                          <component :is="ClockIcon" class="w-5 h-5 text-green-500 left-1 top-1" aria-hidden="true" />
                        </div>
                        <div>
                          {{ (tour.itinerary[0] as any)?.time }} -
                          {{ lastTime }}
                        </div>
                      </div>
                      <div class="flex mb-4 space-x-2 text-sm">
                        <div>
                          <component :is="ShieldCheckIcon" class="w-5 h-5 text-green-500 top-1" aria-hidden="true" />
                        </div>
                        <div>
                          <ul class="space-y-2">
                            <li>Free cancelation</li>
                            <li>Instan confirmation</li>
                            <li>Book now for tommorow</li>
                            <li>Valid on the selected date</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- End Options -->

                  <section class="flex items-center px-4 py-3 bg-gray-800">
                    <div class="w-full text-white">
                      <div class="mb-2 text-base font-semibold">
                        US$
                        <span v-if="!cartStore.getSubTotalById(product_id)">-</span>
                        <span v-else>{{
                          cartStore.getSubTotalById(product_id)
                        }}</span>
                      </div>

                      <button @click="handleAddToCart"
                        class="w-full py-2 font-semibold text-white rounded-lg bg-secondary">
                        Go To Cart
                      </button>
                    </div>
                  </section>
                </div>
              </HeadlessDialogPanel>
            </HeadlessTransitionChild>
          </div>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>
<style scoped>
@keyframes fade-in {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
